import compact from "lodash/compact";
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { v1 as uuid } from "uuid";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconColor } from "../../../../icons/16x16/color-picker.svg";
import { ReactComponent as IconLayout } from "../../../../icons/16x16/layout.svg";
import { ReactComponent as IconCheckFilled } from "../../../../icons/check-filled.svg";
import { Panel } from "../../../../states/customization";
import { TemplateColorDefinition } from "../../../../types/customization/template";
import { evalContentCondition } from "../../../../utils/customization/condition";
import { findCoverTitlePlacement } from "../../../../utils/customization/cover";
import {
  findColorDefinition,
  findPage,
  findPageAndDefinition,
  findPageDefinition,
} from "../../../../utils/customization/finders";
import { canSelectPageDefinitions } from "../../../../utils/customization/pageDefinition";
import EditableBookTitle from "../../../molecules/customization/EditableBookTitle";
import PageLayouts from "../../../molecules/customization/PageLayouts";
import PanelClosing from "../../../molecules/customization/PanelClosing";
import PanelMenuItem from "../../../molecules/customization/PanelMenuItem";
import OptionList from "../../../molecules/customization/panels/OptionList";
import PanelValidation from "../../../molecules/customization/panels/PanelValidation";
import "./style.scss";

interface Props {
  openTab?: string;
  pageKey: string;
}

const CoverPanel: FunctionComponent<Props> = ({ openTab, pageKey }) => {
  const { content, dispatch, template, undoRedo } = useCustomizationContext();
  const intl = useIntl();
  const [panelUndoKey] = useState<string>(`coverPanel${uuid()}`);
  const [initialTitle] = useState<string>(
    content.templateType === "book" ? content.bookTitle || "" : ""
  );

  const hasLayouts = canSelectPageDefinitions(template, content, { type: "front-cover" });

  const currentPage = pageKey ? findPage(pageKey, content) : undefined;

  const hasTitle = useMemo(() => {
    if (content.templateType !== "book") {
      return false;
    }
    if (currentPage) {
      const currentPageDefinition = findPageDefinition(currentPage.pageDefinitionKey, template);
      const currentPageTitleCondtion =
        currentPageDefinition.conditions &&
        currentPageDefinition.conditions.find((condition) => condition.variable === "book_title");
      if (currentPageTitleCondtion && currentPageTitleCondtion.operator === "blank") {
        return false;
      }
    }
    return true;
  }, [content.templateType, currentPage, template]);
  const defaultPanel = hasTitle ? "title" : "options";
  const [openedPanel, setOpenedPanel] = useState<"options" | "title">(defaultPanel);

  useEffect(() => {
    if (openTab && (openTab === "options" || openTab === "title")) {
      if (openTab === "options" && hasLayouts === false) {
        setOpenedPanel(defaultPanel);
      } else {
        setOpenedPanel(openTab);
      }
    }
  }, [defaultPanel, hasLayouts, openTab, setOpenedPanel]);

  const closingOnClickHandler = () => {
    const newTitle = content.templateType === "book" ? content.bookTitle || "" : "";
    if (undoRedo.lastUndoKey === panelUndoKey) {
      dispatch({ type: "undoRedo", way: "undo" });
    } else if (initialTitle !== newTitle) {
      dispatch({
        bookTitle: initialTitle,
        type: "changeBookTitle",
        undoKey: "changeBookTitle",
      });
    }

    dispatch({ type: "setPanel", panel: Panel.CLOSED });
  };

  const validateOnClickHandler = () => {
    dispatch({ type: "setPanel", panel: Panel.CLOSED });
  };

  const titlePlaces = findCoverTitlePlacement(template.pageDefinitions, content.pages);
  const { page, pageDefinition } = useMemo(
    () => findPageAndDefinition(pageKey, template, content),
    [content, pageKey, template]
  );

  const colorItems = useMemo(() => {
    if (!page || !pageDefinition) {
      return null;
    }

    // get definitions of back-cover and spine we should update with front cover
    const relatedDefinitions = template.pageDefinitions.filter(
      (definition) =>
        (definition.type === "spine" || definition.type === "back-cover") &&
        evalContentCondition(definition.conditions || [], template, content)
    );
    const relatedDefinitionKeys = relatedDefinitions.map((definition) => definition.key);
    const relatedPages = content.pages.filter(({ pageDefinitionKey }) =>
      relatedDefinitionKeys.includes(pageDefinitionKey)
    );

    let backgroundKeys = pageDefinition.backgroundColorKeys;
    if (relatedPages.length > 0) {
      backgroundKeys = relatedDefinitions.reduce((finalkeys: string[], relatedDefinition) => {
        return finalkeys.filter((key) => relatedDefinition.backgroundColorKeys.includes(key));
      }, backgroundKeys);
    }
    if (backgroundKeys.length < 2) {
      return null;
    }

    let selectedColors: TemplateColorDefinition | undefined;

    const colorValuesFormatted = backgroundKeys.map((colorKey) => {
      const colorDefinition = findColorDefinition(colorKey, template.colorDefinitions);
      if (!colorDefinition) {
        return null;
      }

      if (colorDefinition.key === page.backgroundColorKey) {
        selectedColors = colorDefinition;
      }

      return {
        key: colorDefinition.key,
        onSelect: () => {
          dispatch({
            colorDefinitionKey: colorDefinition.key,
            pageKey,
            type: "changePageColorDefinition",
            undoKey: panelUndoKey,
          });
          for (const relatedPage of relatedPages) {
            dispatch({
              colorDefinitionKey: colorDefinition.key,
              pageKey: relatedPage.key,
              type: "changePageColorDefinition",
              undoKey: panelUndoKey,
            });
          }
        },
        value: colorDefinition.value,
      };
    });

    return (
      <OptionList
        icon={<IconColor />}
        optionItems={compact(colorValuesFormatted)}
        selectedValue={selectedColors && selectedColors.key}
        title={intl.formatMessage({ id: "kustom_OptionsPanel_color" })}
        type="color"
      />
    );
  }, [content, dispatch, intl, page, pageKey, pageDefinition, panelUndoKey, template]);

  return (
    <div className="CoverPanel">
      <PanelClosing onClick={closingOnClickHandler}>
        <FormattedMessage id="kustom_CoverPanel_cover" />
      </PanelClosing>
      <div className="CoverPanel-icon-menu">
        {hasTitle && (
          <PanelMenuItem
            label={intl.formatMessage({ id: "kustom_CoverPanel_bookTitle" })}
            onClick={() => setOpenedPanel("title")}
            opened={openedPanel === "title"}
            type="type"
          />
        )}
        {(hasLayouts || !!colorItems) && (
          <PanelMenuItem
            label={intl.formatMessage({ id: "kustom_options" })}
            onClick={() => setOpenedPanel("options")}
            opened={openedPanel === "options"}
            type="options"
          />
        )}
      </div>
      <div className="CoverPanel-content">
        {openedPanel === "options" && (
          <>
            {colorItems}

            {hasLayouts && (
              <OptionList
                icon={<IconLayout />}
                optionItems={[]}
                title={intl.formatMessage({
                  id: "kustom_PageEditionPanel_layout",
                })}
                type="layout"
              >
                <PageLayouts
                  currentPageDefinitionKey={currentPage && currentPage.pageDefinitionKey}
                  layoutType="cover"
                  pageKey={pageKey}
                  undoKey={panelUndoKey}
                />
              </OptionList>
            )}
          </>
        )}
        {openedPanel === "title" && (
          <>
            <EditableBookTitle undoKey={panelUndoKey} />
            <section className="CoverPanel-content-places">
              <h3 className="CoverPanel-content-places-title">
                {intl.formatMessage({
                  id: "kustom_CoverPanel_bookTitle_places_title",
                })}
              </h3>
              <div className="CoverPanel-content-places-info">
                {intl.formatMessage({
                  id: "kustom_CoverPanel_bookTitle_places_details",
                })}
              </div>
              <ul className="CoverPanel-content-places-list">
                {titlePlaces.cover && (
                  <li className="CoverPanel-content-places-list-item">
                    <IconCheckFilled />
                    {intl.formatMessage({
                      id: "kustom_CoverPanel_bookTitle_places_cover",
                    })}
                  </li>
                )}
                {titlePlaces.spine && (
                  <li className="CoverPanel-content-places-list-item">
                    <IconCheckFilled />
                    {intl.formatMessage({
                      id: "kustom_CoverPanel_bookTitle_places_spine",
                    })}
                  </li>
                )}
                {titlePlaces.frontMatter && (
                  <li className="CoverPanel-content-places-list-item">
                    <IconCheckFilled />
                    {intl.formatMessage({
                      id: "kustom_CoverPanel_bookTitle_places_flyleaf",
                    })}
                  </li>
                )}
              </ul>
            </section>
          </>
        )}
      </div>
      <div className="CoverPanel-footer">
        <PanelValidation onCancel={closingOnClickHandler} onValidate={validateOnClickHandler} />
      </div>
    </div>
  );
};

export default CoverPanel;
